import type { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import type React from 'react';
import { useCallback } from 'react';
import { appRoutes } from '@fabric-app/business/app/routing/routes.config';
import {
  getUserNavigationHistory,
  userNavigationHistoryStore,
} from '@fabric-app/business/app/stores/userNavigationHistoryStore';
import { Button } from '@fabric-app/ui/components/Button';
import { IconNavigational24BackArrow } from '@fabric-app/icons/svg';

/**
 * paths to which the back button should never lead unless there's override via href or onClick prop
 */
const ignoredPathsFromBackHistory = [
  '/thank-you',
  '/connections-integrations/[integrationId]/success',
  '/connections-integrations/[integrationId]/config',
  '/connections-integrations/error',
  '/connections-integrations/oauth-callback/[provider]',
];

type BackButtonOnNavigate = (e: React.MouseEvent<HTMLButtonElement>) => {
  /**
   * if true, cancels the rest of the navigation flow
   */
  cancelNavigation: boolean;
} | void;

interface ButtonProps extends React.ComponentPropsWithoutRef<typeof Button> {
  href?: LinkProps['href'];
  /**
   * please use this in favour of onClick as it is more clear from the callers point of view
   * whether the navigation logic is being cancelled or not
   */
  onNavigate?: BackButtonOnNavigate;

  /**
   * Extra routes to ignore based on where the back button is used.
   */
  ignoredPaths?: (string | RegExp)[];
}
/**
 *  * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 * KNOWN ISSUE
 * The history tracks both push and replace. Currently there's unknown way to distinguish between them
 * @TODO FUT-3613
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 */
const BackButton = ({ href, onClick, onNavigate, ignoredPaths, ...props }: ButtonProps) => {
  const router = useRouter();

  const handleHover = useCallback(() => {
    // Prefetch the page
    if (href) router.prefetch(href.toString());
  }, [href, router]);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async (e) => {
      /**
       * we're reading the user history from custom implementation because it's not accessible
       * from the router directly (browser API doesn't provide such information because of security reasons)
       */
      const userNavigationHistory = getUserNavigationHistory();

      /******* has onclick, we perform onclick action *******/

      if (onClick) {
        onClick(e);
        return;
      }

      /******* has onNavigate property *******/
      if (onNavigate) {
        const onNavigateRes = onNavigate(e);
        /**
         * if the onNavigate returns cancelNavigation, well, cancel the rest
         * this is usefull if , from the parent component) we just want to add a callback
         * but we want to continue with the default flow
         */
        if (onNavigateRes?.cancelNavigation) {
          return;
        }
      }

      /******* has href override, we navigate user there *******/

      if (href) {
        router.push(href);
        return;
      }

      /******* going back in history *******/

      /**
       * find suitable back path
       */
      const currentAsPathWithoutSearchParams = router.asPath.split('?')[0];
      const backIndex = userNavigationHistory.history.findIndex((item) => {
        /**
         * we get the first history item which has different pathname
         * If we're at /hello?search=world, we're ommiting e.g. /hello?search=something from the comparison as
         * we assume that the user stayed wihin the same context but only the params changed
         */

        const prevAsPathWithoutSearchParams = item.asPath.split('?')[0];

        return (
          prevAsPathWithoutSearchParams !== currentAsPathWithoutSearchParams &&
          !ignoredPathsFromBackHistory.includes(item.pathname) &&
          !ignoredPaths?.some((f) => item.asPath.match(f))
        );
      });

      /**
       * no suitable back path found
       * we take user to the home
       */
      if (backIndex === -1) {
        // we clear the history
        userNavigationHistoryStore.setState(() => ({
          lastState: undefined,
          history: [],
        }));
        router.push(appRoutes.home);
        return;
      }

      /**
       * suitable back path found
       */
      const backPathItem = userNavigationHistory.history[backIndex];

      // clear the history until target point
      userNavigationHistoryStore.setState((state) => ({
        lastState: backPathItem,
        // Removed the + 1 from the slice to keep the backPathItem in the history
        // This is because the useTrackUserNavigationHistory will not recreate it because we set the lastState,
        // so if we e.g. create a space, go back, and create a space again, then go back it will jump 2 steps back
        history: state.history.slice(backIndex),
      }));

      router.push(backPathItem.asPath, undefined, {
        // we use shallow in case the item is just a search param change,
        // or can actually use shallow and make it faster, next.js ignores shallow if not possible
        shallow: true,
      });
    },
    [onClick, onNavigate, href, router, ignoredPaths],
  );

  return (
    <Button
      variant="bg-secondary"
      type="button"
      shape="square"
      onClick={handleClick}
      onMouseEnter={handleHover}
      data-testid="back-button"
      {...props}
    >
      {props.children || <IconNavigational24BackArrow />}
    </Button>
  );
};

export default BackButton;
